
import React from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

export interface PricingFeature {
  text: string;
}

export interface PricingPlanProps {
  name: string;
  description?: string;
  price: string;
  period: string;
  features: string[];
  recommended?: boolean;
  priceId?: string;
  callToAction?: string;
  onSelectPlan?: () => void;
  isProcessing?: boolean;
  isSubscribed?: boolean;
  specialOffer?: {
    enabled: boolean;
    text?: string;
  };
}

const PricingCard: React.FC<PricingPlanProps> = ({
  name,
  description,
  price,
  period,
  features,
  recommended = false,
  priceId,
  callToAction = "Choose Plan",
  onSelectPlan,
  isProcessing = false,
  isSubscribed = false,
  specialOffer = { enabled: false }
}) => {
  const navigate = useNavigate();
  
  const handleAction = () => {
    if (onSelectPlan) {
      onSelectPlan();
    } else {
      navigate("/app/staff");
    }
  };

  return (
    <Card 
      className={`relative transition-all duration-300 ${recommended ? 'border-primary shadow-md' : 'border-border'} hover:shadow-lg`}
    >
      {recommended && (
        <div className="absolute right-2 top-2">
          <Badge className="bg-primary text-white">{recommended ? "Recommended" : null}</Badge>
        </div>
      )}
      
      <CardHeader className="pb-2">
        <CardTitle>{name}</CardTitle>
        {description && <CardDescription className="mt-2">{description}</CardDescription>}
        <div className="flex items-baseline gap-2 mt-4">
          <span className="text-2xl font-bold">{price}</span>
          <span className="text-sm text-muted-foreground">/ {period}</span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {specialOffer.enabled && (
          <div className="flex items-center gap-2 text-sm bg-primary/10 text-primary px-3 py-2 rounded-md">
            <Sparkles className="h-4 w-4" />
            <span>{specialOffer.text || "Special Offer"}</span>
          </div>
        )}
        
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter>
        <Button 
          className="w-full" 
          onClick={handleAction}
          disabled={isProcessing || isSubscribed}
          variant={recommended ? "default" : "outline"}
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : isSubscribed ? (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Currently Subscribed
            </>
          ) : (
            callToAction
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PricingCard;
